import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '../lib/utils'

interface LayoutProps {
  children: ReactNode
}

// MVP Navigation - Core features only
const navigation = [
  { name: 'Dashboard', href: '/', icon: '📊' },
  { name: 'Customers', href: '/customers', icon: '👥' },
  { name: 'Deployments', href: '/deployments', icon: '🚀' },
  { name: 'Configuration', href: '/configuration', icon: '⚙️' },
  // Advanced features moved to legacy:
  // { name: 'Monitoring', href: '/monitoring', icon: '📈' },
  // { name: 'Help & Documentation', href: '/docs', icon: '📘' },
]

export function Layout({ children }: LayoutProps) {
  const location = useLocation()

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                Deployment Manager
                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  MVP
                </span>
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Simplified MVP - Advanced features available in legacy/
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <nav className="w-64 bg-white shadow-sm min-h-screen">
          <div className="p-4">
            <ul className="space-y-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={cn(
                        'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                        isActive
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )}
                    >
                      <span className="mr-3">{item.icon}</span>
                      {item.name}
                    </Link>
                  </li>
                )
              })}
            </ul>
            
            {/* MVP Info */}
            <div className="mt-8 p-3 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-medium text-blue-900 mb-2">MVP Mode</h3>
              <p className="text-xs text-blue-700">
                Advanced features (monitoring, backup, security) are preserved in 
                <code className="bg-blue-100 px-1 rounded">src/legacy/</code> 
                and can be re-enabled with feature flags.
              </p>
            </div>
          </div>
        </nav>

        {/* Main content */}
        <main className="flex-1 p-8">
          {children}
        </main>
      </div>
    </div>
  )
}